import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Heading } from "./NKDaily";
import { Bug, ThumbsUp, Sparkle, Warning, Lightbulb } from "@phosphor-icons/react";

const STATUSES = ["new", "triaged", "in_progress", "fixed", "wontfix", "duplicate"];
const CATEGORIES = ["", "bug", "feature", "idea", "general"];

const CATEGORY_META = {
  bug: { Icon: Bug, color: "text-nk-bad", label: "Bug" },
  feature: { Icon: Sparkle, color: "text-nk-accent", label: "Feature" },
  idea: { Icon: Lightbulb, color: "text-nk-gold", label: "Idea" },
  general: { Icon: ThumbsUp, color: "text-nk-mute", label: "General" },
};

const SEVERITY_TONE = {
  blocker: "bg-nk-bad/20 text-nk-bad",
  high: "bg-nk-warn/20 text-nk-warn",
  medium: "bg-nk-line/40 text-nk-text",
  low: "bg-nk-line/30 text-nk-mute",
};

export default function Feedback() {
  const [data, setData] = useState({ items: [], counts: {}, ack_count: 0 });
  const [status, setStatus] = useState("");
  const [category, setCategory] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/feedback/admin/list", {
        params: { status: status || undefined, category: category || undefined },
      });
      setData(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, category]);

  const setRowStatus = async (id, nextStatus) => {
    try {
      await api.post(`/feedback/admin/${id}/status`, { status: nextStatus });
      toast.success(`Marked ${nextStatus}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-6">
      <Heading
        title="Beta Feedback"
        subtitle="Bug reports, feature requests, and ideas from the 60-day public beta. Email notifications go to the super admin via Resend."
      />

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="feedback-stats">
        <Stat label="Bugs" value={data.counts.bug || 0} tone="bad" Icon={Bug} />
        <Stat label="Features" value={data.counts.feature || 0} tone="accent" Icon={Sparkle} />
        <Stat label="Ideas" value={data.counts.idea || 0} tone="gold" Icon={Lightbulb} />
        <Stat label="General" value={data.counts.general || 0} tone="mute" Icon={ThumbsUp} />
        <Stat label="Got-it taps" value={data.ack_count || 0} tone="accent" />
      </div>

      <div className="flex flex-wrap gap-1.5">
        {CATEGORIES.map((c) => (
          <button key={c || "all"} onClick={() => setCategory(c)}
                  data-testid={`feedback-cat-${c || "all"}`}
                  className={`px-3 py-1.5 text-xs uppercase tracking-wider rounded-sm border ${
                    category === c ? "bg-nk-accent text-nk-bg border-nk-accent" :
                    "text-nk-mute border-nk-line hover:border-nk-accent hover:text-nk-text"
                  }`}>
            {c || "All categories"}
          </button>
        ))}
        <div className="w-full" />
        <button onClick={() => setStatus("")}
                className={`px-3 py-1.5 text-xs uppercase tracking-wider rounded-sm border ${
                  !status ? "bg-nk-accent text-nk-bg border-nk-accent" :
                  "text-nk-mute border-nk-line hover:border-nk-accent hover:text-nk-text"
                }`}>All status</button>
        {STATUSES.map((s) => (
          <button key={s} onClick={() => setStatus(s)}
                  className={`px-3 py-1.5 text-xs uppercase tracking-wider rounded-sm border ${
                    status === s ? "bg-nk-accent text-nk-bg border-nk-accent" :
                    "text-nk-mute border-nk-line hover:border-nk-accent hover:text-nk-text"
                  }`}>
            {s.replace("_", " ")}
          </button>
        ))}
      </div>

      <div className="space-y-2">
        {data.items.length === 0 && (
          <div className="nk-card p-8 text-center">
            <ThumbsUp size={28} weight="duotone" className="text-nk-accent/60 mx-auto mb-3" />
            <p className="text-sm text-nk-mute">No feedback yet{status || category ? " for this filter" : ""}.</p>
          </div>
        )}
        {data.items.map((r) => {
          const meta = CATEGORY_META[r.category] || CATEGORY_META.general;
          return (
            <div key={r.feedback_id} className="nk-card p-4" data-testid={`feedback-row-${r.feedback_id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0 flex-1">
                  <meta.Icon size={18} weight="duotone" className={`shrink-0 mt-0.5 ${meta.color}`} />
                  <div className="min-w-0 flex-1">
                    <p className="font-display text-base font-medium leading-tight">{r.title}</p>
                    <p className="text-[11px] text-nk-mute mt-1 flex items-center gap-2 flex-wrap">
                      <span className={`text-[9px] px-1.5 py-0.5 rounded-sm uppercase tracking-wider ${SEVERITY_TONE[r.severity] || SEVERITY_TONE.medium}`}>
                        {r.severity}
                      </span>
                      <span>· {meta.label}</span>
                      <span>· {r.user_email || "anonymous"}</span>
                      {r.page && <span>· on <span className="font-mono text-nk-text">{r.page}</span></span>}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-sm uppercase tracking-wider ${
                    r.status === "fixed" ? "bg-nk-accent/20 text-nk-accent" :
                    r.status === "wontfix" || r.status === "duplicate" ? "bg-nk-line/40 text-nk-mute" :
                    "bg-nk-warn/20 text-nk-warn"
                  }`}>{(r.status || "new").replace("_", " ")}</span>
                  <select value={r.status || "new"}
                          onChange={(e) => setRowStatus(r.feedback_id, e.target.value)}
                          data-testid={`feedback-status-select-${r.feedback_id}`}
                          className="text-[11px] bg-nk-bg border border-nk-line rounded-sm px-2 py-1">
                    {STATUSES.map((s) => <option key={s} value={s}>{`→ ${s.replace("_", " ")}`}</option>)}
                  </select>
                </div>
              </div>
              <div className="mt-3 p-3 bg-nk-bg/40 border border-nk-line/40 rounded-sm text-sm whitespace-pre-wrap text-nk-text">
                {r.message}
              </div>
              <p className="text-[10px] text-nk-dim mt-2 font-mono flex items-center gap-2 flex-wrap">
                <span>{new Date(r.created_at).toLocaleString()}</span>
                <span>· {r.feedback_id}</span>
                {r.contact_back && <span className="text-nk-accent">· wants reply</span>}
                {r.user_email && (
                  <a href={`mailto:${r.user_email}?subject=Re: ${encodeURIComponent(r.title)}`}
                     className="text-nk-accent hover:underline ml-auto">
                    Reply by email
                  </a>
                )}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Stat({ label, value, tone, Icon }) {
  const color = tone === "bad" ? "text-nk-bad" : tone === "accent" ? "text-nk-accent" : tone === "gold" ? "text-nk-gold" : "text-nk-text";
  return (
    <div className="nk-card p-3">
      <p className="text-[10px] uppercase tracking-wider text-nk-dim flex items-center gap-1">
        {Icon && <Icon size={11} weight="duotone" />} {label}
      </p>
      <p className={`font-display text-2xl mt-1 ${color}`}>{value}</p>
    </div>
  );
}
